import axios from "axios";

/**
 * Same-origin axios client.
 *
 * Cookies:
 *  - eyvo_auth    HttpOnly access JWT (15 min — Session 48 rotation).
 *  - eyvo_refresh HttpOnly refresh JWT (7 days, path=/api/auth) used by
 *                 the interceptor below to silently mint a new access
 *                 cookie when one has naturally expired.
 *  - eyvo_csrf    Readable double-submit token mirrored into the
 *                 X-CSRF-Token header on mutating requests.
 *
 * The browser holds the tokens; JS never reads them directly.
 * `withCredentials: true` makes axios send & receive the cookies.
 */
const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const normalizedBackendUrl = rawBackendUrl.replace(/\/+$/, "");
const API_BASE_URL = normalizedBackendUrl ? `${normalizedBackendUrl}/api` : "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

function readCookie(name) {
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name.replace(/[-.]/g, "\\$&") + "=([^;]+)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

const MUTATING = new Set(["post", "put", "patch", "delete"]);

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (MUTATING.has(method)) {
    const csrf = readCookie("eyvo_csrf");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// ── Silent refresh on 401 ──────────────────────────────────────────────────
// When an access cookie has expired mid-session, the backend returns 401.
// We intercept ONCE, call /api/auth/refresh to mint a fresh access cookie
// (+ rotate the refresh cookie), and retry the original request. Any
// concurrent requests that 401 during the refresh await the in-flight
// promise and then retry, so the user sees no flicker.
//
// If the refresh endpoint itself 401s — reuse detected, session revoked,
// or refresh cookie expired — we fall through to the legacy redirect-to-
// login behaviour.

let refreshInFlight = null;

function isAuthRefreshPath(url) {
  // Don't try to refresh when the refresh endpoint or login endpoints
  // themselves 401. Guarding against infinite loops.
  if (!url) return false;
  return (
    url.includes("/auth/refresh") ||
    url.includes("/auth/login") ||
    url.includes("/auth/google") ||
    url.includes("/auth/verify-totp") ||
    url.includes("/auth/verify-recovery-code") ||
    url.includes("/auth/logout")
  );
}

async function performSilentRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";

    if (status === 401 && !original._retried && !isAuthRefreshPath(url)) {
      original._retried = true;
      try {
        await performSilentRefresh();
        // Re-mirror the (possibly fresh) CSRF token and retry.
        if (MUTATING.has((original.method || "get").toLowerCase())) {
          const csrf = readCookie("eyvo_csrf");
          if (csrf) {
            original.headers = { ...(original.headers || {}), "X-CSRF-Token": csrf };
          }
        }
        return api(original);
      } catch (_) {
        // Refresh failed — session is truly dead. Fall through to the
        // legacy behaviour of bouncing the user to the landing page.
      }
    }

    if (status === 401) {
      // Don't redirect when the failing call IS the AuthContext bootstrap
      // probe (`/auth/me`) or any auth-helper path — those return 401 by
      // design for unauthenticated viewers, and redirecting on them traps
      // anyone trying to visit a PUBLIC page (e.g. `/guide/client`) while
      // signed out.
      if (isAuthRefreshPath(url) || url.includes("/auth/me")) {
        return Promise.reject(error);
      }
      // Pages that are intentionally reachable without auth — don't bounce
      // the viewer back to the landing page if they're already on one.
      const PUBLIC_PATHS = ["/guide/client", "/guide/internal", "/login", "/signup"];
      const here = window.location.pathname;
      const onPublicPage = PUBLIC_PATHS.some((p) => here === p || here.startsWith(p + "/"));
      if (window.location.pathname !== "/" && !onPublicPage) {
        // Session 69 — preserve the deep-link path through the
        // login flow. Pre-fix this stripped the query and dumped
        // the user on the bare landing page after sign-in, even if
        // they'd clicked "Open Ticket SUP-0044" in an email. Now we
        // capture the path as `?next=` so LoginPage can route them
        // back after auth.
        const target = window.location.pathname + (window.location.search || "");
        const next = encodeURIComponent(target);
        window.location.href = `/?next=${next}`;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
